/* =========================================================
   VIORA AI — GHARYARD
   Live Front-end Controller
   AI Chat + Secure Shopify Order Tracking
   Version 4.0
   ========================================================= */

(() => {
  "use strict";

  const root = document.getElementById("viora-ai");
  if (!root) return;

  const launcher = document.getElementById("viora-launcher");
  const panel = document.getElementById("viora-panel");
  const closeButton = document.getElementById("viora-close");
  const chatBody = document.getElementById("viora-chat-body");
  const messagesContainer = document.getElementById("viora-messages");
  const form = document.getElementById("viora-form");
  const input = document.getElementById("viora-input");
  const sendButton = document.getElementById("viora-send");
  const whatsappButton = document.getElementById("viora-whatsapp");
  const contextScript = document.getElementById("viora-context");
  const productQuestionsContainer =
    document.getElementById("viora-product-questions");

  const BASE_WORKER_URL =
    root.dataset.workerBase ||
    "https://gharyard-viora-ai.wispy-block-5506.workers.dev";

  const CHAT_URL =
    root.dataset.workerUrl ||
    `${BASE_WORKER_URL}/chat`;

  const ORDER_TRACK_URL =
    `${BASE_WORKER_URL}/order-track`;

  const WHATSAPP_NUMBER =
    String(root.dataset.whatsappNumber || "916290250125")
      .replace(/\D/g, "");

  const MAX_HISTORY_MESSAGES = 8;
  const REQUEST_TIMEOUT = 25000;

  let isOpen = false;
  let isSending = false;
  let conversationHistory = [];
  let typingElement = null;
  let activeRequestController = null;
  let positionFrame = null;
  let trackingBox = null;

  /* PAGE CONTEXT */

  function readPageContext() {
    if (!contextScript) {
      return {
        pageType: "",
        pageUrl: window.location.href,
        product: null
      };
    }

    try {
      const parsed =
        JSON.parse(contextScript.textContent || "{}");

      parsed.pageUrl =
        parsed.pageUrl || window.location.href;

      return parsed;
    } catch (error) {
      console.warn("[Viora] Could not parse storefront context:", error);

      return {
        pageType: "",
        pageUrl: window.location.href,
        product: null
      };
    }
  }

  const pageContext = readPageContext();

  if (pageContext.pageType === "product") {
    root.classList.add("viora-product-page");
  }

  /* PRODUCT-SPECIFIC SHOPPER QUESTIONS */

  function productCorpus(product) {
    if (!product) return "";

    const variants = Array.isArray(product.variants)
      ? product.variants.map(item => item?.title || "").join(" ")
      : "";

    const options = Array.isArray(product.options)
      ? product.options
          .map(item => `${item?.name || ""} ${(item?.values || []).join(" ")}`)
          .join(" ")
      : "";

    return [
      product.title,
      product.description,
      product.productType,
      product.tags,
      variants,
      options
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  }

  function questionItem(label, prompt, icon = "spark") {
    return { label, prompt, icon };
  }

  function getProductQuestionSet(product) {
    const corpus = productCorpus(product);
    const title = String(product?.title || "this product");

    const verifiedPrompt = question =>
      `${question} Answer specifically for "${title}" using only verified information from the current Gharyard product page. If an exact detail is not available, say that clearly and give the safest useful guidance without guessing.`;

    // Adjustable rods / curtain rods / tension rods
    if (/\b(rod|curtain rod|tension rod|adjustable rod)\b/.test(corpus)) {
      return [
        questionItem("What size does it adjust to?", verifiedPrompt("What is the exact adjustable size or length range of this product?"), "ruler"),
        questionItem("Will it fit my space?", verifiedPrompt("How can I check whether this will fit my space? Explain the relevant measurements shown on the page."), "fit"),
        questionItem("Does it need drilling?", verifiedPrompt("Does this product require drilling, screws or permanent fixing? How is it installed?"), "tool"),
        questionItem("How do I install it?", verifiedPrompt("Explain how to install this product step by step using the verified product information."), "tool"),
        questionItem("Where can I use it?", verifiedPrompt("What are the verified recommended use cases or places where this product can be used?"), "home"),
        questionItem("How strong is it?", verifiedPrompt("What does the product page say about strength, load, stability or suitable curtain use? Do not invent a weight limit."), "shield")
      ];
    }

    // Scalp oil / massage comb
    if (/\b(scalp|oil comb|massage comb|hair oil|applicator comb|scalivra)\b/.test(corpus)) {
      return [
        questionItem("How do I fill it with oil?", verifiedPrompt("How do I fill the oil reservoir or applicator correctly?"), "drop"),
        questionItem("How do I use it?", verifiedPrompt("Explain the correct way to use this product on the scalp step by step."), "spark"),
        questionItem("Can I use my own hair oil?", verifiedPrompt("Can I use my regular hair oil with this product? Answer only from the verified product information."), "drop"),
        questionItem("Is it reusable?", verifiedPrompt("Is this product reusable or refillable, and what does the page say about repeated use?"), "repeat"),
        questionItem("How do I clean it?", verifiedPrompt("What verified cleaning or care instructions are available for this product?"), "clean"),
        questionItem("What's included?", verifiedPrompt("What exactly comes in the package or box with this product?"), "box")
      ];
    }

    // Faucet / tap extenders
    if (/\b(faucet|tap|extender|flowtwist|spray mode)\b/.test(corpus)) {
      return [
        questionItem("Will it fit my tap?", verifiedPrompt("What tap or faucet compatibility information is provided, and how can I check if it will fit mine?"), "fit"),
        questionItem("How do I install it?", verifiedPrompt("How is this faucet extender installed? Give the verified steps."), "tool"),
        questionItem("What are the spray modes?", verifiedPrompt("What spray or water-flow modes does this product have and what is each used for?"), "spark"),
        questionItem("Does it rotate 360°?", verifiedPrompt("What does the product page say about rotation or movement range?"), "repeat"),
        questionItem("Will it reduce splashing?", verifiedPrompt("What does the page say about splash reduction and everyday use?"), "drop"),
        questionItem("Where can I use it?", verifiedPrompt("What verified kitchen, bathroom or other use cases are listed for this product?"), "home")
      ];
    }

    // Magnetic door mosquito net
    if (/\b(mosquito|magnetic door|door shield|breezeshield|mesh door)\b/.test(corpus)) {
      return [
        questionItem("Will it fit my door?", verifiedPrompt("What door size or fit information is provided, and how should I measure my door?"), "fit"),
        questionItem("What size is it?", verifiedPrompt("What are the exact verified dimensions of this product?"), "ruler"),
        questionItem("How does auto-close work?", verifiedPrompt("How does the magnetic auto-closing feature work according to the product page?"), "spark"),
        questionItem("Does it need drilling?", verifiedPrompt("Does installation require drilling or permanent fixing? Explain the verified installation method."), "tool"),
        questionItem("Can extra length be folded?", verifiedPrompt("Can extra material or length be folded or adjusted? Use only the product page information."), "fit"),
        questionItem("Is it reusable?", verifiedPrompt("Is the product reusable and what does the page say about removing or reinstalling it?"), "repeat")
      ];
    }

    // AC outdoor unit covers
    if (/\b(ac cover|air conditioner|outdoor unit|condenser|protector cover)\b/.test(corpus)) {
      return [
        questionItem("Which size should I choose?", verifiedPrompt("How do I choose the correct size or tonnage option for my AC outdoor unit?"), "ruler"),
        questionItem("What tonnage does it fit?", verifiedPrompt("What AC tonnage range or size compatibility is stated on this product page?"), "fit"),
        questionItem("How do I install it?", verifiedPrompt("How is this cover installed or fitted? Use only verified page information."), "tool"),
        questionItem("Can I use it outdoors?", verifiedPrompt("What does the page say about outdoor use and protection?"), "home"),
        questionItem("What's included?", verifiedPrompt("What quantity or package contents are included with this product?"), "box"),
        questionItem("Which option is right for me?", verifiedPrompt("Help me choose the correct available product option based on the verified variants. Ask for my AC tonnage if needed."), "spark")
      ];
    }

    // Generic product-aware question ranking
    const questions = [];
    const add = (condition, item) => {
      if (condition && !questions.some(q => q.label === item.label)) {
        questions.push(item);
      }
    };

    add(
      /(size|dimension|length|width|height|cm|mm|inch|feet|\bft\b|adjustable)/.test(corpus),
      questionItem("What size is it?", verifiedPrompt("What are the exact verified dimensions, size or adjustable range?"), "ruler")
    );

    add(
      /(fit|universal|compatible|compatibility)/.test(corpus),
      questionItem("Will it fit my setup?", verifiedPrompt("What compatibility or fit information is provided, and how can I check if it suits my setup?"), "fit")
    );

    add(
      /(install|installation|mount|attach|assemble|drill|adhesive|setup)/.test(corpus),
      questionItem("How do I install it?", verifiedPrompt("How do I install or set up this product? Give only verified steps."), "tool")
    );

    add(
      /(material|steel|metal|plastic|silicone|fabric|mesh|cotton|stainless)/.test(corpus),
      questionItem("What is it made of?", verifiedPrompt("What verified material or construction information is shown for this product?"), "shield")
    );

    add(
      /(wash|washable|clean|reusable|refill|refillable)/.test(corpus),
      questionItem("How do I clean / reuse it?", verifiedPrompt("What cleaning, care, reuse or refill information is verified for this product?"), "clean")
    );

    add(
      /(mode|rotate|rotation|magnet|auto|massage|adjustable|feature)/.test(corpus),
      questionItem("How does it work?", verifiedPrompt("Explain how the main features of this product work using only verified information."), "spark")
    );

    add(
      /(box|package|included|pcs|pieces|set|pack|dropper)/.test(corpus),
      questionItem("What's included in the box?", verifiedPrompt("What exactly is included in the product package?"), "box")
    );

    add(
      Array.isArray(product?.variants) && product.variants.length > 1,
      questionItem("Which option should I choose?", verifiedPrompt("Compare the available variants or options and help me choose the right one based only on verified information."), "fit")
    );

    const fallbacks = [
      questionItem("What are the main benefits?", verifiedPrompt("What are the main verified benefits and useful everyday use cases of this product?"), "spark"),
      questionItem("How do I use it?", verifiedPrompt("How should this product be used according to the verified product information?"), "tool"),
      questionItem("Will it suit my needs?", verifiedPrompt("Help me decide if this product suits my needs. Ask me one question about my intended use if necessary."), "fit"),
      questionItem("What's included?", verifiedPrompt("What is included with this product or in its package?"), "box"),
      questionItem("Is COD available?", `Is Cash on Delivery available for "${title}"? Use only verified Gharyard delivery and COD information.`, "card"),
      questionItem("Can I return it?", `What is Gharyard's current return/refund policy if "${title}" does not suit me? Use the latest verified policy.`, "repeat")
    ];

    for (const item of fallbacks) {
      if (questions.length >= 6) break;
      if (!questions.some(q => q.label === item.label)) {
        questions.push(item);
      }
    }

    return questions.slice(0, 6);
  }

  function productQuestionIcon(type) {
    const icons = {
      ruler: '<path d="M4 8h16v8H4V8Zm4 0v3m4-3v2m4-2v3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>',
      fit: '<path d="M7 3H3v4M17 3h4v4M7 21H3v-4M17 21h4v-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
      tool: '<path d="M14.5 6.4a4 4 0 0 0-5 5L4 17l3 3 5.5-5.5a4 4 0 0 0 5-5L15 12l-3-3 2.5-2.6Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
      home: '<path d="m4 11 8-7 8 7v9H4v-9Zm5 9v-6h6v6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
      shield: '<path d="M12 3 19 6v5.5c0 4.2-2.7 7.6-7 9.5-4.3-1.9-7-5.3-7-9.5V6l7-3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
      drop: '<path d="M12 3s5 5.8 5 10a5 5 0 1 1-10 0c0-4.2 5-10 5-10Z" fill="none" stroke="currentColor" stroke-width="1.7"/>',
      repeat: '<path d="M7 7h10l-2.5-2.5M17 17H7l2.5 2.5M19 7a7 7 0 0 1 1 3M5 17a7 7 0 0 1-1-3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
      clean: '<path d="M6 18h12M8 15l6-9 3 2-6 9H8v-2Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
      box: '<path d="m4 7 8-4 8 4-8 4-8-4Zm0 0v10l8 4 8-4V7M12 11v10" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
      card: '<path d="M3 6h18v12H3V6Zm0 4h18M7 14h4" fill="none" stroke="currentColor" stroke-width="1.7"/>',
      spark: '<path d="M12 3c.8 3.1 2.9 5.2 6 6-3.1.8-5.2 2.9-6 6-.8-3.1-2.9-5.2-6-6 3.1-.8 5.2-2.9 6-6Z" fill="currentColor"/>'
    };

    return `<svg viewBox="0 0 24 24" aria-hidden="true">${icons[type] || icons.spark}</svg>`;
  }

  function renderProductQuestions() {
    if (
      !productQuestionsContainer ||
      pageContext.pageType !== "product" ||
      !pageContext.product
    ) {
      return;
    }

    const questions =
      getProductQuestionSet(pageContext.product);

    productQuestionsContainer.replaceChildren();

    questions.forEach((question, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "viora-product-question";
      button.dataset.vioraPrompt = question.prompt;

      const icon = document.createElement("span");
      icon.className = "viora-product-question-icon";
      icon.innerHTML = productQuestionIcon(question.icon);

      const label = document.createElement("span");
      label.className = "viora-product-question-text";
      label.textContent = question.label;

      const arrow = document.createElement("span");
      arrow.className = "viora-product-question-arrow";
      arrow.textContent = "›";
      arrow.setAttribute("aria-hidden", "true");

      button.append(icon, label, arrow);
      productQuestionsContainer.appendChild(button);

      button.style.setProperty(
        "--viora-question-delay",
        `${index * 38}ms`
      );
    });
  }

  function shouldAutoFocusInputs() {
    return window.matchMedia(
      "(min-width: 750px) and (pointer: fine)"
    ).matches;
  }

  /* OPEN / CLOSE */

  function openChat() {
    isOpen = true;

    root.classList.add("viora-open");
    panel.classList.add("is-open");

    panel.setAttribute("aria-hidden", "false");
    launcher.setAttribute("aria-expanded", "true");

    document.documentElement.classList.add("viora-chat-active");

    if (shouldAutoFocusInputs()) {
      window.setTimeout(() => {
        input.focus({ preventScroll: true });
      }, 220);
    }

    scrollToBottom(false);
  }

  function closeChat() {
    isOpen = false;

    root.classList.remove("viora-open");
    panel.classList.remove("is-open");

    panel.setAttribute("aria-hidden", "true");
    launcher.setAttribute("aria-expanded", "false");

    document.documentElement.classList.remove("viora-chat-active");

    launcher.focus({ preventScroll: true });

    updateLauncherPosition();
  }

  launcher.addEventListener("click", openChat);
  closeButton.addEventListener("click", closeChat);

  document.addEventListener("keydown", event => {
    if (event.key === "Escape" && isOpen) {
      closeChat();
    }
  });

  /* GENERAL HELPERS */

  function scrollToBottom(smooth = true) {
    if (!chatBody) return;

    requestAnimationFrame(() => {
      chatBody.scrollTo({
        top: chatBody.scrollHeight,
        behavior: smooth ? "smooth" : "auto"
      });
    });
  }

  function formatDate(value) {
    if (!value) return null;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  function normalizeCourierName(value) {
    if (!value) return "Courier partner";

    const raw = String(value).trim();
    const lower = raw.toLowerCase();

    if (lower.includes("xpressbees")) return "Xpressbees";
    if (lower.includes("shadowfax")) return "Shadowfax";
    if (lower.includes("delhivery")) return "Delhivery";
    if (lower.includes("ekart")) return "Ekart";
    if (lower.includes("ecom")) return "Ecom Express";
    if (lower.includes("bluedart") || lower.includes("blue dart")) return "Blue Dart";
    if (lower.includes("india post")) return "India Post";

    return raw
      .replace(/new\d*gm/gi, "")
      .replace(/\d+gm/gi, "")
      .trim();
  }

  function normalizeShipmentStatus(shipment, order) {
    const raw =
      String(
        shipment?.latestEvent?.statusCode ||
        shipment?.statusCode ||
        shipment?.status ||
        ""
      )
        .trim()
        .toUpperCase();

    if (raw.includes("OUT_FOR_DELIVERY")) return "Out for Delivery";
    if (raw.includes("IN_TRANSIT")) return "In Transit";
    if (raw.includes("DELIVERED")) return "Delivered";
    if (raw.includes("FAIL")) return "Delivery Exception";
    if (raw.includes("ATTEMPT")) return "Delivery Attempted";
    if (raw.includes("PICK")) return "Picked Up";

    if (raw === "FULFILLED" || order?.shipped) {
      return "Shipped";
    }

    return shipment?.status || order?.fulfillmentStatus || "Processing";
  }

  /* MESSAGE UI */

  function createMessageElement(role, text) {
    const message = document.createElement("div");

    message.className =
      role === "user"
        ? "viora-message viora-message-user"
        : "viora-message viora-message-ai";

    message.textContent = String(text || "");
    message.style.whiteSpace = "pre-wrap";
    message.style.wordBreak = "break-word";

    return message;
  }

  function addMessage(role, text) {
    if (!text) return null;

    const wrapper = document.createElement("div");

    wrapper.style.display = "flex";
    wrapper.style.width = "100%";
    wrapper.style.justifyContent =
      role === "user" ? "flex-end" : "flex-start";

    wrapper.appendChild(
      createMessageElement(role, text)
    );

    messagesContainer.appendChild(wrapper);
    scrollToBottom();

    return wrapper;
  }

  /* TYPING INDICATOR */

  function showTyping() {
    removeTyping();

    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.justifyContent = "flex-start";

    const typing = document.createElement("div");
    typing.className = "viora-typing";
    typing.innerHTML = "<span></span><span></span><span></span>";

    wrapper.appendChild(typing);
    messagesContainer.appendChild(wrapper);

    typingElement = wrapper;
    scrollToBottom();
  }

  function removeTyping() {
    if (!typingElement) return;

    typingElement.remove();
    typingElement = null;
  }

  /* TEXTAREA */

  function resizeTextarea() {
    input.style.height = "auto";
    input.style.height =
      `${Math.min(input.scrollHeight, 100)}px`;
  }

  input.addEventListener("input", resizeTextarea);

  input.addEventListener("keydown", event => {
    if (
      event.key === "Enter" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      if (!isSending) {
        form.requestSubmit();
      }
    }
  });

  /* CONVERSATION HISTORY */

  function addToHistory(role, content) {
    conversationHistory.push({ role, content });

    if (conversationHistory.length > MAX_HISTORY_MESSAGES) {
      conversationHistory =
        conversationHistory.slice(-MAX_HISTORY_MESSAGES);
    }
  }

  /* AI REQUEST */

  function setSendingState(state) {
    isSending = state;
    sendButton.disabled = state;

    input.setAttribute(
      "aria-busy",
      state ? "true" : "false"
    );
  }

  async function requestViora(message, history) {
    if (activeRequestController) {
      activeRequestController.abort();
    }

    const controller = new AbortController();
    activeRequestController = controller;

    const timeout =
      window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT
      );

    try {
      const response =
        await fetch(CHAT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            message,
            context: pageContext,
            history: history.slice(-MAX_HISTORY_MESSAGES)
          }),
          signal: controller.signal
        });

      let data;

      try {
        data = await response.json();
      } catch {
        throw new Error("Viora returned an invalid response.");
      }

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
          "Viora is temporarily unavailable."
        );
      }

      if (
        typeof data.reply !== "string" ||
        !data.reply.trim()
      ) {
        throw new Error("Viora could not generate a reply.");
      }

      return data.reply.trim();
    } finally {
      clearTimeout(timeout);

      if (activeRequestController === controller) {
        activeRequestController = null;
      }
    }
  }

  async function sendMessage(text) {
    const message = String(text || "").trim();

    if (!message || isSending) return;

    addMessage("user", message);

    const priorHistory =
      [...conversationHistory];

    setSendingState(true);
    input.value = "";
    resizeTextarea();
    showTyping();

    try {
      const reply =
        await requestViora(message, priorHistory);

      removeTyping();

      addMessage("assistant", reply);

      addToHistory("user", message);
      addToHistory("assistant", reply);
    } catch (error) {
      removeTyping();

      console.error("[Viora AI]", error);

      const errorText =
        error?.name === "AbortError"
          ? "The request took a little too long. Please try again."
          : "I'm having trouble connecting right now. Please try again.";

      addMessage("assistant", errorText);
    } finally {
      setSendingState(false);

      if (shouldAutoFocusInputs()) {
        input.focus({ preventScroll: true });
      }
    }
  }

  form.addEventListener("submit", async event => {
    event.preventDefault();
    await sendMessage(input.value);
  });

  /* ORDER TRACKING */

  function removeTrackingBox() {
    if (!trackingBox) return;

    trackingBox.remove();
    trackingBox = null;
  }

  function createTrackOrderForm() {
    removeTrackingBox();

    trackingBox = document.createElement("div");
    trackingBox.className = "viora-tracking-box";

    const heading = document.createElement("div");
    heading.className = "viora-tracking-heading";
    heading.textContent = "Track your order";

    const subheading = document.createElement("div");
    subheading.className = "viora-tracking-subheading";
    subheading.textContent =
      "Enter your Order ID and registered phone number or email.";

    const trackForm = document.createElement("form");
    trackForm.className = "viora-tracking-form";
    trackForm.autocomplete = "off";

    const orderInput = document.createElement("input");
    orderInput.className = "viora-tracking-input";
    orderInput.type = "text";
    orderInput.placeholder = "Order ID — e.g. GY3049";
    orderInput.autocomplete = "off";
    orderInput.maxLength = 60;

    const contactInput = document.createElement("input");
    contactInput.className = "viora-tracking-input";
    contactInput.type = "text";
    contactInput.placeholder = "Registered phone or email";
    contactInput.autocomplete = "off";
    contactInput.maxLength = 200;

    const trackButton = document.createElement("button");
    trackButton.className = "viora-tracking-submit";
    trackButton.type = "submit";
    trackButton.textContent = "Track Order";

    const errorBox = document.createElement("div");
    errorBox.className = "viora-tracking-error";

    trackForm.append(
      orderInput,
      contactInput,
      trackButton,
      errorBox
    );

    trackingBox.append(
      heading,
      subheading,
      trackForm
    );

    messagesContainer.appendChild(trackingBox);
    scrollToBottom();

    if (shouldAutoFocusInputs()) {
      window.setTimeout(() => {
        orderInput.focus({ preventScroll: true });
      }, 100);
    }

    trackForm.addEventListener("submit", async event => {
      event.preventDefault();

      const orderId = orderInput.value.trim();
      const contact = contactInput.value.trim();

      if (!orderId || !contact) {
        errorBox.textContent =
          "Please enter both Order ID and registered phone number/email.";
        errorBox.classList.add("is-visible");
        return;
      }

      errorBox.classList.remove("is-visible");

      trackButton.disabled = true;
      trackButton.textContent = "Checking order...";

      try {
        const result =
          await requestOrderTracking(orderId, contact);

        renderTrackingResult(result.order);
      } catch (error) {
        console.error("[Viora Tracking]", error);

        errorBox.textContent =
          error.message ||
          "We couldn't verify this order. Please check the details and try again.";

        errorBox.classList.add("is-visible");
      } finally {
        trackButton.disabled = false;
        trackButton.textContent = "Track Order";
      }
    });
  }

  async function requestOrderTracking(orderId, contact) {
    const controller = new AbortController();

    const timeout =
      window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT
      );

    try {
      const response =
        await fetch(ORDER_TRACK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            orderId,
            contact
          }),
          signal: controller.signal
        });

      let data;

      try {
        data = await response.json();
      } catch {
        throw new Error(
          "Tracking service returned an invalid response."
        );
      }

      if (!response.ok || !data.ok) {
        switch (data.code) {
          case "NOT_VERIFIED":
            throw new Error(
              "We couldn't verify that order. Please check your Order ID and registered phone number/email."
            );

          case "TOO_MANY_ATTEMPTS":
            throw new Error(
              "Too many tracking attempts. Please try again later."
            );

          case "CUSTOMER_DATA_ACCESS_REQUIRED":
            throw new Error(
              "Secure order verification is temporarily unavailable. Please contact Gharyard Support."
            );

          default:
            throw new Error(
              data.error ||
              "Order tracking is temporarily unavailable."
            );
        }
      }

      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  function createStatusIcon() {
    const icon = document.createElement("span");
    icon.className = "viora-tracking-check";
    icon.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12.5 10 16l8-9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return icon;
  }

  function addTrackingRow(container, label, value) {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return;
    }

    const row = document.createElement("div");
    row.className = "viora-tracking-row";

    const left = document.createElement("span");
    left.className = "viora-tracking-label";
    left.textContent = label;

    const right = document.createElement("strong");
    right.className = "viora-tracking-value";
    right.textContent = String(value);

    row.append(left, right);
    container.appendChild(row);
  }

  function renderTrackingResult(order) {
    removeTrackingBox();

    trackingBox = document.createElement("div");
    trackingBox.className = "viora-tracking-box";

    if (!order) return;

    if (order.cancelled) {
      const status = document.createElement("div");
      status.className =
        "viora-tracking-status is-cancelled";

      status.append(
        createStatusIcon(),
        document.createTextNode("Order Cancelled")
      );

      trackingBox.appendChild(status);

      addTrackingRow(
        trackingBox,
        "Order ID",
        order.name
      );

      addTrackingRow(
        trackingBox,
        "Cancelled on",
        formatDate(order.cancelledAt)
      );

      messagesContainer.appendChild(trackingBox);
      scrollToBottom();
      return;
    }

    if (
      !order.shipped ||
      !Array.isArray(order.shipments) ||
      !order.shipments.length
    ) {
      const status = document.createElement("div");
      status.className = "viora-tracking-status";

      status.append(
        createStatusIcon(),
        document.createTextNode("Order Confirmed")
      );

      const info = document.createElement("div");
      info.className = "viora-tracking-info";
      info.textContent =
        "Your order has not been shipped yet. Tracking details will appear here once the shipment is created.";

      trackingBox.append(status, info);

      addTrackingRow(
        trackingBox,
        "Order ID",
        order.name
      );

      addTrackingRow(
        trackingBox,
        "Status",
        order.fulfillmentStatus
      );

      messagesContainer.appendChild(trackingBox);
      scrollToBottom();
      return;
    }

    const shipment = order.shipments[0];

    const currentStatus =
      normalizeShipmentStatus(
        shipment,
        order
      );

    const delivered =
      currentStatus === "Delivered" ||
      Boolean(shipment.deliveredAt);

    const status = document.createElement("div");

    status.className =
      delivered
        ? "viora-tracking-status is-delivered"
        : "viora-tracking-status";

    status.append(
      createStatusIcon(),
      document.createTextNode(
        delivered
          ? "Order Delivered"
          : "Order Shipped"
      )
    );

    trackingBox.appendChild(status);

    addTrackingRow(
      trackingBox,
      "Order ID",
      order.name
    );

    addTrackingRow(
      trackingBox,
      "Current Status",
      currentStatus
    );

    addTrackingRow(
      trackingBox,
      "Delivery Partner",
      normalizeCourierName(
        shipment.deliveryPartner
      )
    );

    addTrackingRow(
      trackingBox,
      "Tracking ID",
      shipment.trackingNumber
    );

    if (shipment.deliveredAt) {
      addTrackingRow(
        trackingBox,
        "Delivered On",
        formatDate(shipment.deliveredAt)
      );
    } else {
      addTrackingRow(
        trackingBox,
        "Estimated Delivery",
        formatDate(
          shipment.estimatedDeliveryAt
        ) || "Not available yet"
      );
    }

    if (shipment.latestEvent?.message) {
      addTrackingRow(
        trackingBox,
        "Latest Update",
        shipment.latestEvent.message
      );
    }

    if (shipment.trackingUrl) {
      const trackLink =
        document.createElement("a");

      trackLink.className = "viora-track-link";
      trackLink.href = shipment.trackingUrl;
      trackLink.target = "_blank";
      trackLink.rel = "noopener noreferrer";
      trackLink.textContent = "Track Shipment";

      trackingBox.appendChild(trackLink);
    }

    const note = document.createElement("div");
    note.className = "viora-tracking-note";
    note.textContent = "Order details verified securely.";

    trackingBox.appendChild(note);

    messagesContainer.appendChild(trackingBox);
    scrollToBottom();
  }

  /* QUICK ACTIONS */

  root.addEventListener("click", async event => {
    const promptButton =
      event.target.closest("[data-viora-prompt]");

    if (promptButton) {
      const prompt =
        promptButton.dataset.vioraPrompt;

      if (prompt) {
        await sendMessage(prompt);
      }

      return;
    }

    const actionButton =
      event.target.closest("[data-viora-action]");

    if (!actionButton) return;

    const action =
      actionButton.dataset.vioraAction;

    if (action === "track-order") {
      createTrackOrderForm();
      return;
    }

    if (action === "support") {
      openWhatsApp(
        "Hi Gharyard Support, I need help with an order or product."
      );
    }
  });

  /* WHATSAPP */

  function openWhatsApp(customMessage) {
    if (!WHATSAPP_NUMBER) {
      addMessage(
        "assistant",
        "WhatsApp support is currently unavailable."
      );
      return;
    }

    const text =
      encodeURIComponent(
        customMessage ||
        "Hi Gharyard Support, I need assistance."
      );

    const url =
      `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  }

  whatsappButton.addEventListener("click", () => {
    let message =
      "Hi Gharyard Support, I need assistance.";

    if (
      pageContext.pageType === "product" &&
      pageContext.product
    ) {
      message =
        `Hi Gharyard Support, I need help with this product: ${pageContext.product.title}. ${pageContext.pageUrl}`;
    }

    openWhatsApp(message);
  });

  /* STICKY BUY NOW BAR */

  function elementIsVisible(element) {
    if (!element) return false;

    const style = getComputedStyle(element);

    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return false;
    }

    const rect =
      element.getBoundingClientRect();

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  }

  function findFixedAncestor(element) {
    let node = element;

    for (
      let depth = 0;
      node &&
      node !== document.body &&
      depth < 10;
      depth++
    ) {
      const style = getComputedStyle(node);

      if (
        style.position === "fixed" ||
        style.position === "sticky"
      ) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  function findStickyBuyBar() {
    if (pageContext.pageType !== "product") {
      return null;
    }

    const candidates =
      document.querySelectorAll(
        "button,a,[role='button'],input[type='submit']"
      );

    const patterns = [
      "buy now",
      "cash on delivery",
      "buy now - cash on delivery",
      "buy now – cash on delivery"
    ];

    for (const candidate of candidates) {
      if (!elementIsVisible(candidate)) {
        continue;
      }

      const text =
        (
          candidate.textContent ||
          candidate.value ||
          ""
        )
          .trim()
          .toLowerCase();

      if (!text) continue;

      const matches =
        patterns.some(pattern =>
          text.includes(pattern)
        );

      if (!matches) continue;

      const fixed =
        findFixedAncestor(candidate);

      if (!fixed) continue;

      const rect =
        fixed.getBoundingClientRect();

      if (
        rect.bottom >=
          window.innerHeight - 12 &&
        rect.top >
          window.innerHeight * 0.45
      ) {
        return fixed;
      }
    }

    return null;
  }

  function updateLauncherPosition() {
    if (positionFrame) {
      cancelAnimationFrame(positionFrame);
    }

    positionFrame =
      requestAnimationFrame(() => {
        positionFrame = null;

        if (pageContext.pageType !== "product") {
          launcher.style.removeProperty("bottom");
          return;
        }

        const stickyBar =
          findStickyBuyBar();

        if (!stickyBar) {
          launcher.style.removeProperty("bottom");
          return;
        }

        const rect =
          stickyBar.getBoundingClientRect();

        const barHeight =
          Math.max(
            0,
            window.innerHeight - rect.top
          );

        const gap =
          window.innerWidth <= 749
            ? 14
            : 16;

        launcher.style.bottom =
          `calc(${Math.round(
            barHeight + gap
          )}px + env(safe-area-inset-bottom))`;
      });
  }

  window.addEventListener(
    "resize",
    updateLauncherPosition,
    { passive: true }
  );

  window.addEventListener(
    "scroll",
    updateLauncherPosition,
    { passive: true }
  );

  if (window.visualViewport) {
    window.visualViewport.addEventListener(
      "resize",
      updateLauncherPosition
    );
  }

  const stickyObserver =
    new MutationObserver(() => {
      updateLauncherPosition();
    });

  stickyObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "class",
        "style",
        "hidden"
      ]
    }
  );

  /* INITIALIZE */

  function initialize() {
    root.classList.remove("viora-open");
    panel.classList.remove("is-open");

    panel.setAttribute("aria-hidden", "true");
    launcher.setAttribute("aria-expanded", "false");

    resizeTextarea();
    renderProductQuestions();
    updateLauncherPosition();

    window.setTimeout(updateLauncherPosition, 450);
    window.setTimeout(updateLauncherPosition, 1100);
    window.setTimeout(updateLauncherPosition, 2400);

    console.info(
      "[Viora AI] Premium live assistant loaded."
    );
  }

  initialize();
})();
